import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from './ui/Button';
import { RoomSelection } from './steps/RoomSelection';
import { ProblemDescription } from './steps/ProblemDescription';
import { ReviewSubmit } from './steps/ReviewSubmit';
import { useLanguage } from '../contexts/LanguageContext';
interface ReportWizardProps {
  category: string;
  onBack: () => void;
  onSubmit: (data: any) => void;
}
export const ReportWizard: React.FC<ReportWizardProps> = ({
  onSubmit,
  onBack
}) => {
  const {
    t
  } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    room: '',
    description: '',
    isBlocking: null as boolean | null,
    photos: [] as string[]
  });
  const steps = [{
    number: 1,
    label: t.wizard.steps.room
  }, {
    number: 2,
    label: t.wizard.steps.details
  }, {
    number: 3,
    label: t.wizard.steps.review
  }];
  const handleRoomSelect = (room: string) => {
    setFormData({
      ...formData,
      room
    });
    setCurrentStep(2);
  };
  const handleDescriptionChange = (description: string) => {
    setFormData({
      ...formData,
      description
    });
  };
  const handlePhotosChange = (photos: string[]) => {
    setFormData({
      ...formData,
      photos
    });
  };
  const handleIsBlockingChange = (isBlocking: boolean) => {
    setFormData({
      ...formData,
      isBlocking
    });
  };
  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    } else {
      onSubmit(formData);
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      onBack();
    }
  };
  const canProceed = () => {
    if (currentStep === 1) return !!formData.room;
    if (currentStep === 2) {
      return formData.photos.length > 0 && formData.isBlocking !== null && formData.description.length >= 20 && formData.description.length <= 1000;
    }
    if (currentStep === 3) return true;
    return true;
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-480">
      {/* Progress Indicator */}
      <div className="bg-white border-b border-gray-200 px-8 py-8 relative z-0" data-id="element-481">
        <div className="max-w-3xl mx-auto" data-id="element-482">
          <div className="flex items-start justify-between relative" data-id="element-483">
            {steps.map((step, index) => <div key={step.number} className="flex flex-col items-center flex-1 relative" data-id="element-484">
                {/* Connecting Line */}
                {index < steps.length - 1 && <div className="absolute left-1/2 top-7 w-full h-1.5 -z-10" data-id="element-485">
                    <div className="h-full bg-gray-200 rounded-full" data-id="element-486">
                      <motion.div className="h-full bg-teal-deep rounded-full" initial={{
                  width: '0%'
                }} animate={{
                  width: currentStep > step.number ? '100%' : '0%'
                }} transition={{
                  duration: 0.4,
                  ease: 'easeInOut'
                }} data-id="element-487" />
                    </div>
                  </div>}

                {/* Step Circle */}
                <motion.div className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-semibold transition-colors relative z-10 ${currentStep === step.number ? 'bg-teal-deep text-white' : currentStep > step.number ? 'bg-teal-deep text-white' : 'bg-white text-gray-400 border-2 border-gray-300'}`} initial={{
              scale: 0.8
            }} animate={{
              scale: currentStep === step.number ? 1.1 : 1
            }} transition={{
              duration: 0.3,
              ease: 'easeOut'
            }} data-id="element-488">
                  {step.number}
                </motion.div>

                {/* Step Label */}
                <motion.span className={`mt-3 text-sm font-medium transition-colors ${currentStep === step.number ? 'text-teal-deep' : currentStep > step.number ? 'text-gray-700' : 'text-gray-400'}`} initial={{
              opacity: 0,
              y: -5
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} data-id="element-489">
                  {step.label}
                </motion.span>
              </div>)}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 px-8 py-10 overflow-y-auto" data-id="element-490">
        <div className="max-w-3xl mx-auto" data-id="element-491">
          <AnimatePresence mode="wait" data-id="element-492">
            <motion.div key={currentStep} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-493">
              {currentStep === 1 && <RoomSelection selectedRoom={formData.room} onSelectRoom={handleRoomSelect} data-id="element-494" />}
              {currentStep === 2 && <ProblemDescription description={formData.description} photos={formData.photos} isBlocking={formData.isBlocking} onDescriptionChange={handleDescriptionChange} onPhotosChange={handlePhotosChange} onIsBlockingChange={handleIsBlockingChange} data-id="element-495" />}
              {currentStep === 3 && <ReviewSubmit formData={formData} data-id="element-496" />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 py-6 px-8 z-40" data-id="element-497">
        <div className="max-w-3xl mx-auto flex justify-between items-center gap-4" data-id="element-498">
          <Button variant="secondary" onClick={currentStep > 1 ? handleBack : onBack} className="h-14 px-8 text-lg flex items-center gap-2" data-id="element-499">
            <ArrowLeft className="w-5 h-5" data-id="element-500" />
            {t.common.back}
          </Button>

          <Button onClick={handleNext} disabled={!canProceed()} className="h-14 px-8 text-lg bg-teal-deep hover:bg-teal-700 text-white font-semibold flex items-center gap-2" data-id="element-501">
            {currentStep === 3 ? t.common.submit : t.common.next}
            {currentStep !== 3 && <ArrowRight className="w-5 h-5" data-id="element-502" />}
          </Button>
        </div>
      </div>
    </div>;
};